import { AppData, Colors, Img, Lightning, Log, Registry, Router, Utils } from '@lightningjs/sdk'
import { AppSetup } from '../lib/app-setup'
import GridViewModel, { FocusInfo } from '../lib/components/grid/grid-view-model'
import MultiRowGrid from '../lib/components/grid/multirow-grid'
import InfoUI from '../ui-elements/info-ui'
import { ItemNode } from "../lib/components/grid/ItemNode"
import { Logger } from '../lib/logger'
import { AppApiConfig, Channel } from '../lib/app-api-config'
import { Helper } from '../lib/helper'
import { KeyCodes } from '../lib/key-codes'
import { IDialogueInfo } from '../lib/interfaces'
import { DIALOG_BUTTON_ACTION, DIALOG_TYPE, GRIDTYPE } from '../lib/enum'
import { Autoplay } from '../lib/autoplay'
import TopCarouselUi from '../ui-elements/top-carousel'
import childApp from '../lib/child-app'


export interface MainGridTemplateSpec extends Lightning.Component.TemplateSpecLoose {
    Background: object
    focusRow: number
    RowListWrapper: object
    MultiRowGrid: object
    TopCarouselGrid: object
    Logo: object
    activeL1Load: Function
}

export interface MainGridTypeConfig extends Lightning.Component.TypeConfig {
    IsPage: true
}

export default class MainGrid
    extends Lightning.Component<MainGridTemplateSpec, MainGridTypeConfig>
    implements Lightning.Component<MainGridTemplateSpec>
{
    channel!: Channel
    Background = this.getByRef('Background')!
    RowListWrapper = this.getByRef('RowListWrapper')!
    MultiRowGrid = this.getByRef('MultiRowGrid')!
    TopCarouselGrid = this.getByRef('TopCarousel')!
    Logo = this.getByRef('Logo')!
    
    gridViewModel!: GridViewModel
    focusRow = 0

    loadingTimeoutId = null;
    isMenuFocused = false;

    autoPlay!: Autoplay
    autoPlayStoppedByBack = false;
    isTopCarouselRowAvailable = false;
    
    longPressTimestamp = 0;
    isKeyDown = false;
    notFoundMsgNoSeconds = 5000; // 5 seconds
    isResolvingItem = false;

    private _bgImage: string | null = null;
    private _moreBgImage: string | null = null;

    static override _template(): Lightning.Component.Template<MainGridTemplateSpec> {
        return {
            ...super._template(),
            w: 1920,
            h: 1080,
            rect: true,
            color: 0xff222222,
            Background: {
                rect: true,
                collision:true,
                w: 1920,
                h: 1080,
                color: 0xff222222,
            },
            ItemInfo: {
                w: 1920,
                h: 1080,
                type: InfoUI,
            },
            TopCarousel:{
                type: TopCarouselUi,
                collision: true,
                visible:false
            },
            Logo: {
                x: 172,
                y: 66,
            },
            RowListWrapper: {
                x: 12,
                y: 515,
                w: 1920,
                h: 565,
                collision: true,
                boundsMargin: [0, 0, 0, 0],
                clipping: true,
                zIndex: 5,
                MultiRowGrid: undefined
            },
            NodeNotFoundMsgBox: {
                w: (w: any) => w,
                h: (h: any) => h,
                color: 0xee000000,
                rect: true,
                zIndex: 100,
                flex: { alignItems: 'center', justifyContent: 'center' },
                MsgBox: {
                  text: {
                    text: "Sorry, the content you're looking for is currently not available, please try again later!",
                    wordWrapWidth: 600,
                    fontFace: Helper.getFontFamily('Black'),
                    letterSpacing: 0.82,
                    lineHeight: 47,
                    fontSize: 36,
                  }
                },
                visible: false
              }
        }
    }

    override _init() {
        //this.widgets.loadingscreen.showL1LoadingScreen(false)
    }

    /**
     * @method initAutoplay
     * @description Initialize the autoplay feature for the grid.
     * 
     */
    initAutoplay() {
        const { appState, appConfig } = AppData as AppSetup;
        const appApiConfig = appState?.getAppApiConfig()!
        const autoplayTimer = appApiConfig?.appConfig?.autoplayTimer * 1000

        const heroImageAutoplay = appApiConfig?.appConfig.heroImageAutoplay;
        const enableTrailer = appApiConfig?.appConfig.enableTrailer;

        if ((heroImageAutoplay && appConfig?.enableAutoplay) || (enableTrailer && (appConfig?.enableAutoplay || appConfig?.enableTrailer))) {
            this.autoPlay = new Autoplay({
                gridDelay: autoplayTimer,
                stopOnInteraction: false,
                gridType: 'main'
            })
            this.autoPlayStoppedByBack = false
            this.autoPlay.on('onStart', () => {
                Log.info('MainGrid: onStart')
            })
            this.autoPlay.on('onGridAutoplay', (vodInfo, heroPreview) => {
                Log.info('MainGrid: onGridAutoplay started', vodInfo, heroPreview)
                this.updateAutoPlayVideo(true)
                if (heroPreview?.playShorts) {
                    Logger.processUserAction('shortsStarted', vodInfo.node_id);
                } else if (heroPreview?.playTrailer) {
                    Logger.processUserAction('trailerStarted', vodInfo.node_id);
                } else {
                    Logger.processUserAction('heroImageAutoplayStarted', vodInfo.node_id)
                }
                if (appConfig?.enableMidCarousel && this._getCurrentRowType() === 'carousel') {
                    const focusPath = this.application?.focusPath;
                    const activeTile: any = focusPath ? focusPath[focusPath.length - 1] : null;
                    if (activeTile && typeof activeTile._startVideoPreview === 'function') {
                        activeTile._startVideoPreview();
                    }
                }
            })
            this.autoPlay.on('onVideoAutoplay', (vodInfo) => {
                Log.info('MainGrid: onVideoAutoplay started', vodInfo)
                this.checkAutoplay(vodInfo)
            })
            this.autoPlay.on('onAutoplayStopped', (vodInfo, heroPreview) => {
                Log.info('MainGrid: onAutoplayStopped', heroPreview)
                this.updateAutoPlayVideo(false)
                if (vodInfo) {
                    if (heroPreview?.playShorts) {
                        Logger.processUserAction('shortsStopped', vodInfo.node_id);
                    } else if (heroPreview?.playTrailer) {
                        Logger.processUserAction('trailerStopped', vodInfo.node_id);
                    } else {
                        Logger.processUserAction('heroImageAutoplayStopped', vodInfo.node_id);
                    }
                }
            })
        }
    }

    /**
     * @method updateAutoPlayVideo
     * @description Update the video playing status for autoplay.
     * @param {boolean} statusVideoPlaying - Video playing status.
     * @returns - NA
     * 
     */
    updateAutoPlayVideo(statusVideoPlaying = false) {
        if (statusVideoPlaying) {
            this.patch({
                color: 0x00222222,
                Background: {
                    color: 0x00222222,
                },
                ItemInfo: {
                    gridVideoPlaying: true,
                }
            })
        } else {
            this.patch({
                color: 0xff222222,
                Background: {
                    color: 0xff222222,
                },
                ItemInfo: {
                    gridVideoPlaying: false,
                }
            })
        }
    }

    override _active(): void {
        Router.focusPage()
        //this.widgets.loadingscreen.showL1LoadingScreen(true)
    }

    $gridFocusChange() {
        Log.info('$gridFocusChange')
        //this._gridFocusedHandler(null)
    }

    override _getFocused():
        | Lightning.Component<Lightning.Component.TemplateSpecLoose, Lightning.Component.TypeConfig>
        | null
        | undefined {
        return this.tag('MultiRowGrid')!
    }

    override _focus(): void {
        Log.info('MainGridTemplateSpec: _focus fired')
        this.autoPlay?.userInteracted(true)

        //this.widgets.Loading.showL1LoadingScreen(true)
    }

    override _unfocus(): void {
        this.updateAutoPlayVideo(false)
    }

    reloadGrid() {
        this.tag('MultiRowGrid').reloadGrid()
    }

    resetFocus(focusObj: FocusInfo) {
        this.tag('MultiRowGrid').resetFocus(focusObj)
    }

    override _handleKey(e: KeyboardEvent): boolean | void {
       // console.log('_handleKey : ', e)
        Log.info('MainGridTemplateSpec: _handleKey ', e)
        const { platform, appConfig } = AppData as AppSetup
        //XFIN-162 - Comcast - Implement alternative way to open star menu for PlatCo
        //Open option menu when 0 is pressed.
        if(e.keyCode == KeyCodes.KEY_LONG_OK) {
            const rowType = this._getCurrentRowType()
            const isCarouselOrNonClick = (appConfig?.enableMidCarousel && (rowType === 'carousel' || rowType === 'non-click')) || rowType === 'gridbanner'

            // Only stop autoplay if NOT on mid carousel, non-click or grid-banner row
            if (!isCarouselOrNonClick) {
                this.autoPlay?.userInteracted(false)
            }
            this.$handleLongEnter()
            return true
        }
        if(platform?.isOptionsKey(e)) {
            const rowType = this._getCurrentRowType()
            const isCarouselOrNonClick = (appConfig?.enableMidCarousel && (rowType === 'carousel' || rowType === 'non-click')) || rowType === 'gridbanner'

            // Only stop autoplay if NOT on mid carousel, non-click or grid-banner row
            if (!isCarouselOrNonClick) {
                this.autoPlay?.userInteracted(false)
            }
            platform?.textToSpeach('Opening Options Menu')
            return this.$handleOptionsKey()
        }
        if(e.keyCode == KeyCodes.KEY_EXIT) {
            const { appState } = AppData as AppSetup
            const app = appState?.getApp()
            app?.handleAppExit()
            return true
        }
        // if(Helper.getAppType() && 
        //     (Helper.getAppType().indexOf('platco') > -1 ||
        //     Helper.getAppType().indexOf('vizio') > -1 ||
        //     Helper.getAppType().indexOf('lg') > -1)
        // ) {
        //     if (e.code == 'Digit0') {
        //         platform?.textToSpeach('Opening Options Menu')
        //         Router.focusWidget('OptionsMenu')
        //         return true
        //     }
        // } else {
        //     if (e.code == 'KeyI') {
        //         platform?.textToSpeach('Opening Options Menu')
        //         Router.focusWidget('OptionsMenu')
        //         return true
        //     }
        // }
        return false
    }

    _gridLoadedHandler(event: any) {
        Log.info('MainGridTemplateSpec: gridViewModel: _gridLoadedHanlder: ', event)
        Log.info('MainGridTemplateSpec: gridViewModel: ', this.gridViewModel)
        Logger.processUserAction('gridContentLoaded', '')
        if(!this.gridViewModel._isRowsLoaded()) {
            this.isRowsLoaded = false
            this.tag('NodeNotFoundMsgBox').visible = true
        } else {
            this.isRowsLoaded = true
            const { appState, appConfig } = AppData as AppSetup
            const app = appState?.getApp();
            this.isTopCarouselRowAvailable = app!.isTopCarouselVisible && !appConfig?.disableTopCarousel
            if(this.isTopCarouselRowAvailable){
                this.patch({
                    TopCarousel:{
                        w:1920,
                        h:1080,
                        grid: app?.topCarouselGrid!,
                        visible: true,
                    },
                })
                if(!appConfig?.disableTopCarouselFocus){
                    this._setState('TopCarouselState')
                } else {
                    this._setState('MultiRowGridState')
                }
            } else {
                this._setState('MultiRowGridState')
            }
            if(appConfig?.disableTopCarouselFocus){
                appConfig.disableTopCarouselFocus = false
            }
            this.patch({
                RowListWrapper: {
                    MultiRowGrid: {
                        x: 172,
                        y: 0,
                        w: (w: any) => w,
                        h: (h: any) => h,
                        grid:this.gridViewModel,
                        collision: true,
                        //item: this.gridViewModel,
                        type: MultiRowGrid,
                        items: [],
                        itemSpacing: 10,
                    }
                }
            })
        }
        this.loadingTimeoutId = Registry.setTimeout(() => {
            //this.widgets.loading
            this.widgets.loadingscreen.showL1LoadingScreen(false)
            this.initAutoplay()
            this.autoPlay && this.autoPlay?.userInteracted(true)
        }, 1000)
    }

    _gridFocusedHandler(event: any) {
        const { platform, appState } = AppData as AppSetup;
        const vodInfo: ItemNode = this.gridViewModel.getFocusedItem();
        Log.info('MainGridTemplateSpec:_gridFocusedHandler: ', this.gridViewModel.getFocusInfo(), vodInfo)
        this.patch({
            ItemInfo: {
                item: vodInfo,
            }
        })
        this.autoPlay && this.autoPlay.userInteracted(true)
        platform?.textToSpeach(`${vodInfo.title}  ${vodInfo.description}`)

        /* Fix for XFIN-1190 */
        // Show sorry popup if deeplink not found
        if (appState.showDeeplinkSorryMsgOnGrid) {
            this.showHideMessage(true)
            appState.showDeeplinkSorryMsgOnGrid = false
            Registry.setTimeout(() => {
                this.showHideMessage(false)
            }, this.notFoundMsgNoSeconds)
        }
        /* Fix for XFIN-1190 */
    }

    /**
     * Get the current row type from the grid view model
     * @private
     */
    private _getCurrentRowType(rowIndex?: number): string | null {
        const currentRowIndex = rowIndex ?? this.gridViewModel.getFocusInfo().focusRow
        if (this.gridViewModel && this.gridViewModel.uiList && this.gridViewModel.uiList[currentRowIndex]) {
            const row = this.gridViewModel.uiList[currentRowIndex]
            if (row && row.data && row.data.item && row.data.item.rowList) {
                return row.data.item.rowList.row_type
            }
        }
        return null
    }

    async _gridSelectedHandler(event: any) {
        if (this.isResolvingItem) return;
        this.autoPlay?.userInteracted(false);

        let vodInfo: ItemNode = this.gridViewModel.getFocusedItem();
        const { appState, platform, appConfig } = AppData as AppSetup
        const app = appState?.getApp()
        const { focusRow, foucsItem } = this.gridViewModel.getFocusInfo()
        Log.info('MainGridTemplateSpec:_gridSelectedHandler: ', this.gridViewModel.getFocusInfo(), vodInfo)

        if (!vodInfo) {
            Log.error('MainGridTemplateSpec:_gridSelectedHandler: no focused item at', focusRow, foucsItem)
            return;
        }

        if(this.checkBlockVodInfo(vodInfo)) {
            return;
        }
        
        const rowType = this._getCurrentRowType(focusRow)

        // Check if current row is non-click or grid-banner type and block navigation
        if ((appConfig?.enableMidCarousel && rowType === 'non-click') || rowType === 'gridbanner') {
            Log.info('MainGridTemplateSpec: Navigation blocked for row type', rowType)
            return;
        }

        if (app?.blockingManager.checkAndShowAgeRatingPopup(vodInfo, (actionBtn: any, dialogInfo: any) => this._handleSimpleAgeRestrictionCallback(actionBtn, dialogInfo))) {
            return;
        }

        let pendingGrids: GridViewModel[] = [];

        if (appConfig?.enableMidCarousel && rowType === 'carousel') {
            this.isResolvingItem = true;
            this.widgets.loadingscreen.showL1LoadingScreen(true);
            try {
                const result = await this._resolvePlayableItem(vodInfo);
                if (!result) return;
                vodInfo = result.item;
                pendingGrids = result.grids;
            } catch (e) {
                Log.error('MainGridTemplateSpec: Error resolving playable item', e);
                return;
            } finally {
                this.isResolvingItem = false;
                this.widgets.loadingscreen.showL1LoadingScreen(false);
            }
            if (app?.blockingManager.checkAndShowAgeRatingPopup(vodInfo, (actionBtn: any, dialogInfo: any) => this._handleSimpleAgeRestrictionCallback(actionBtn, dialogInfo))) {
                return;
            }
        }
        if (pendingGrids.length > 0 && app?.navigation) {
            pendingGrids.forEach(g => {
                if (!app.navigation.continuesStack.includes(g)) {
                    app.navigation.push(g);
                }
            });
        }

        this.checkBookmarkOrVideoResume(vodInfo)
        platform?.textToSpeach(`Playing ${vodInfo.title}`)
    }
    /**
     * @method checkBlockVodInfo
     * @description Check if the vodInfo is blocked and show the dialog if it is
     * @param vodInfo ItemNode
     * @returns boolean
     */
    checkBlockVodInfo(vodInfo: ItemNode) {
        if (vodInfo.blockItem) {
            const dialogInfo: IDialogueInfo = {
                title: 'Uh-Oh',
                body: 'Please unblock to play content',
                type: DIALOG_TYPE.ALERT,
                buttons: [
                    {
                        w: 200,
                        name: 'OK',
                        index: 0,
                        action: DIALOG_BUTTON_ACTION.CLOSE,
                        initialFocus: true,
                    }
                ]
            }
            this.widgets.dialog.showDialog(dialogInfo)
            Router.focusWidget('dialog')
            return true
        }
        return false
    }

    /** 
    * @method _resolvePlayableItem
    * @description Recursively resolve the playable item from a container (e.g. ListOfList/Series)
    * This enables "Direct Play" from the Mid Carousel, skipping the MLP page.
    */
    async _resolvePlayableItem(item: ItemNode, depth = 0, grids: GridViewModel[] = []): Promise<{item: ItemNode, grids: GridViewModel[]} | null> {
        if (depth > 5) return {item, grids};

        if (item.type === 'listoflist' || item.type === 'new-qid' || item.feed_type === 'banner') {
            const { appState } = AppData as AppSetup;
            const app = appState?.getApp();

            try {
                const mlpGrid: GridViewModel = (app?.loadShowPrefetch(item, item.id))!
                
                if (mlpGrid) {
                    await mlpGrid._init();
                    mlpGrid.createGridListUI();
                    
                    grids.push(mlpGrid);
                    
                    const focusedItem = mlpGrid.getFocusedItem();
                    if (this.checkBlockVodInfo(focusedItem)) {
                        return null;
                    }
                    return await this._resolvePlayableItem(focusedItem, depth + 1, grids);
                }
            } catch (e) {
                Log.error('MainGridTemplateSpec: Failed to resolve playable item', e);
                return {item, grids};
            }
        }

        return {item, grids};
    }

    _handleSimpleAgeRestrictionCallback(actionBtn:any, dialogInfo:IDialogueInfo) {
        //console.log('_handleSimpleAgeRestrictionCallback: ', actionBtn.action)
        const { appState, appConfig } = AppData as AppSetup
        const app = appState?.getApp()
        if(actionBtn.action == DIALOG_BUTTON_ACTION.YES) {
            this.checkBookmarkOrVideoResume(dialogInfo.callbackData)
        } else if(actionBtn.action == DIALOG_BUTTON_ACTION.NO) {
            // app?.navigation.reset()
            // appConfig.aContent = false
            // Router.navigate('main-grid-screen/' + app?.l1CategoryToLoad + '_true', false)
            app?.reloadAppOnParentalControlChange(false)
        } else if (actionBtn.action == DIALOG_BUTTON_ACTION.CLOSE) {
            //reset the isAgePopupShown to false as the user have not select any options. 
            appConfig.isAgePopupShown = false;
        }
    }

    _handleBookmarking(vodInfo) {
        this.widgets.bookmark.$setbdata(vodInfo)
        Router.focusWidget('Bookmark')
    }

    _handleVideoResume(vodInfo) {
        const { appConfig, appState } = AppData as AppSetup;
        const appApiConfig = appState?.getAppApiConfig()
        let resumeTime = null
        if (appConfig?.useLocalStorage == true) {
            resumeTime = localStorage.getItem(vodInfo.node_id)!;
        } else {
            resumeTime = Helper.getCookie(vodInfo.node_id);
        }

        if (resumeTime !== "" && resumeTime !== null && resumeTime !== 0 && resumeTime !== '0' && (appApiConfig?.appConfig?.resumeVidMinDuration && vodInfo.runtime > appApiConfig?.appConfig?.resumeVidMinDuration)) {
            this.widgets.bookmark.$setbdata(vodInfo)
            this.widgets.menu.hideMenu()
            Router.focusWidget('Bookmark')
        } else {
            Router.navigate('video-screen/' + vodInfo.node_id + '_resume', false)
        }
    }

    /**
    * @method checkAutoplay
    * @description Check the video type to set the route for playing.
    * For live items nothing was previewed in the hero, so this is the point where autoplay
    * takes the user into the live player.
    * @param {number} vodInfo - Video info.
    * @returns - NA
    */
    checkAutoplay(vodInfo) {
        if (Helper.isChildAppItem(vodInfo)) {
            Log.info('MainGridTemplateSpec: checkAutoplay skipped for childapp item', vodInfo?.site_id)
            return
        }
        if (vodInfo.type == 'listoflist' || vodInfo.type == 'new-qid' || vodInfo.feed_type == 'banner' || vodInfo.feed_type == 'video') {
            Router.navigate('video-screen/' + vodInfo.node_id + '_resume', false)
        } else if (vodInfo.feed_type == 'live') {
            Logger.processUserAction('liveAutoplayStarted', vodInfo.node_id)
            Router.navigate('video-screen-live/' + vodInfo.node_id, false)
        } else if (vodInfo.type == 'listoflist' || vodInfo.type == 'new-qid' || vodInfo.feed_type == 'banner' || vodInfo.feed_type == 'video') {
            Router.navigate('video-screen/' + vodInfo.node_id + '_resume', false)
        }
    }

    /**
     * @method resetAutoplay
     * @description Reset the autoplay feature by removing event listeners and destroying the instance.
     */
    resetAutoplay() {
        if (this.autoPlay) {
            this.autoPlay.off('onStart');
            this.autoPlay.off('onGridAutoplay');
            this.autoPlay.off('onVideoAutoplay');
            this.autoPlay.off('onAutoplayStopped');
            this.autoPlay.destroy();
            this.autoPlay = null
        }
    }
    
    /* Fix for XFIN-1723 */
    /**
     * @method launchChildApp
     * @description Open the site behind an "app suggestions" tile inside the app, reusing the
     * same child-app stack the bug-ad launchInAppOpening action uses: a fresh AppModel for the
     * target site is pushed on appState.appStack and the router lands on that site's main grid.
     * The hero preview is torn down first so nothing keeps playing across the switch.
     * @param {ItemNode} vodInfo - The focused childapp node, carrying the target site_id.
     * @returns {boolean} true when the site switch was started.
     */
    launchChildApp(vodInfo: ItemNode): boolean {
        const { appState, platform } = AppData as AppSetup
        const app = appState?.getApp()
        const siteId = vodInfo?.site_id ? String(vodInfo.site_id) : ''

        if (!Helper.isValidValue(siteId) || siteId == '0') {
            Log.error('MainGridTemplateSpec: launchChildApp - invalid site id for', vodInfo?.title, siteId)
            return false
        }
        // Compare against the platform-remapped id (loadChildApp remaps too), so a legacy
        // site id pointing back at the site we are already running is not stacked on itself.
        const targetSiteId = platform ? platform.remapSiteId(siteId) : siteId
        if (app?.siteId == targetSiteId) {
            Log.info('MainGridTemplateSpec: launchChildApp - already running site', targetSiteId)
            return false
        }

        Logger.processUserAction('newChildAppOpenedOnGrid', siteId)

        this.autoPlay?.userInteracted(false)
        this.updateAutoPlayVideo(false)

        childApp.loadChildApp({ site_id: siteId })
        return true
    }

    /**
     * Handler to show bookmark screen or video resume screen.
     * @param {number} vodInfo - Video info.
     * @returns - NA
     */
    checkBookmarkOrVideoResume(vodInfo, force=false) {
        if(force) {
            this.widgets.moreinfo.$setbdata(vodInfo)
            Router.focusWidget('Moreinfo')
            return;
        }
        if (Helper.isChildAppItem(vodInfo)) {
            this.launchChildApp(vodInfo)
            return;
        }
        this.autoPlay && this.autoPlay.userInteracted(false)
        if (vodInfo.type == 'listoflist' || vodInfo.type == 'new-qid' || vodInfo.feed_type == 'banner') {
            Logger.processUserAction('showClickedOnGrid', vodInfo.id);
            //Router.navigate(`mlp-grid-screen/${vodInfo.id}`, false)
            this._handleBookmarking(vodInfo)
        } else if (vodInfo.feed_type == 'video') {
            Logger.processUserAction('videoItemSelectedFromGrid', vodInfo.node_id);
            this._handleVideoResume(vodInfo)
            //Router.navigate('video-screen/' + vodInfo.node_id + '_resume', false)
        } else if (vodInfo.feed_type == 'live') {
            Logger.processUserAction('videoItemSelectedFromGrid', vodInfo.node_id);
            // Live has no hero preview stage, so autoplay would drag the user straight back into the
            // channel they just backed out of. Consume it for this session on manual play too.
            this.autoPlay?.markAutoplayIgnored(vodInfo)
            Router.navigate('video-screen-live/' + vodInfo.node_id, false)
        }
    }


    override _onDataProvided(): void {
        const { appState } = AppData as AppSetup
        const app = appState?.getApp()
        const appApiConfig: AppApiConfig = appState?.getAppApiConfig()!
        const channel = appApiConfig.appConfig.channels[app?.l1CategoryToLoad]
        let bgImage = Utils.asset('/images/info_bg-236.png');
        if(channel && channel.bg_image) {
            bgImage = channel.bg_image
        }
        let cgImage = null
        if(channel && channel.cg_image){
            cgImage = channel.cg_image
        }
        let moreBgImage = null;
        if(channel && channel.moreBgImage){
            moreBgImage = channel.moreBgImage
        }
        this._bgImage = bgImage
        this._moreBgImage = moreBgImage

        Log.info('MainGridTemplateSpec: _onDataProvided fired')
        this.gridViewModel.on('loaded', this._gridLoadedHandler, this)
        this.gridViewModel.on('focused', this._gridFocusedHandler, this)
        this.gridViewModel.on('selected', this._gridSelectedHandler, this)
        this.gridViewModel._init()



        this.patch({
            TopCarousel:{
                background: cgImage
            },
            ItemInfo: {
                //background: Utils.asset('/images/info_bg-236.png'),
                background: bgImage,
            },
            Logo: {
                texture: {
                    src: app?.appApiConfig?.appBrandConfig?.header_logo,
                    resizeMode: {
                        h: 60,
                        w: 460,
                        type: 'contain',
                    },
                    type: Lightning.textures.ImageTexture
                },
            }
        })
        this.widgets.loadingscreen.showL1LoadingScreen(true)
    }

    $topCarouselEmpty() {
        this.isTopCarouselRowAvailable = false;
        this.patch({
            TopCarousel: {
                visible: false
            }
        });
        this._setState('MultiRowGridState');
    }

    $showLoadingScreen(show: boolean) {
        this.widgets.loadingscreen.showL1LoadingScreen(show);
    }

    /**
     * @method $onCarouselFocused
     * @description Handle the focus event from the top carousel.
     * @returns - NA
     * 
     */
    $onCarouselFocused() {
        const { appState } = AppData as AppSetup
        const app = appState?.getApp()
        if (app?.navigation.getCurrentGrid()?.type === GRIDTYPE.TOP_CAROUSEL) {
            app?.navigation.continuesStack.pop()
        }
        this._setState('MultiRowGridState')
    }

    /**
     * @method $punchHole
     * @description Apply a hole shader to the background and item info.
     * @param {any} rect - The rectangle to apply the hole shader to.
     * @returns - NA
     */
    $punchHole(rect: any) {
        this.tag('Background').patch({
            shader: {
                type: Lightning.shaders.Hole,
                x: rect.x,
                y: rect.y,
                w: rect.w,
                h: rect.h,
                radius: 10
            }
        });
        
        this.tag('ItemInfo').patch({
            ImageWrapper: {
                shader: {
                    type: Lightning.shaders.Hole,
                    x: rect.x,
                    y: rect.y,
                    w: rect.w,
                    h: rect.h,
                    radius: 10
                }
            },
            NodeImg: {
                shader: {
                    type: Lightning.shaders.Hole,
                    x: rect.x - (1920 - 1244),
                    y: rect.y,
                    w: rect.w,
                    h: rect.h,
                    radius: 10
                }
            }
        });
    }

    /**
     * @method $removeHole
     * @description Remove the hole shader from the background and item info.
     * @returns - NA
     */
    $removeHole() {
        this.tag('Background').patch({
            shader: null
        });
        this.tag('ItemInfo').patch({
            ImageWrapper: {
                shader: null
            },
            NodeImg: {
                shader: null
            }
        });
    }

    /**
     * @method $handleCorouselSelectedItem
     * @description Handle the selected item from the top carousel.
     * @param {ItemNode} vodInfo - The selected item.
     * @param {GridViewModel[]} pendingGrids - The pending grids.
     * @returns - NA
     */
    $handleCorouselSelectedItem(vodInfo: ItemNode, pendingGrids: GridViewModel[] = []) {
        const { appState, platform } = AppData as AppSetup
        const app = appState?.getApp()
        if (app?.topCarouselGrid) {
            if (app?.blockingManager.checkAndShowAgeRatingPopup(vodInfo, (actionBtn: any, dialogInfo: any) => this._handleSimpleAgeRestrictionCallback(actionBtn, dialogInfo, pendingGrids))) {
                return;
            }
            if (vodInfo.blockItem) {
                const dialogInfo: IDialogueInfo = {
                    title: 'Uh-Oh',
                    body: 'Please unblock to play content',
                    type: DIALOG_TYPE.ALERT,
                    buttons: [
                        {
                            w: 200,
                            name: 'OK',
                            index: 0,
                            action: DIALOG_BUTTON_ACTION.CLOSE,
                            initialFocus: true,
                        }
                    ]
                }
                this.widgets.dialog.showDialog(dialogInfo)
                Router.focusWidget('dialog')
                return true
            }
            Logger.processUserAction('CarouselImpression_TransitionToVideoScreen', vodInfo.node_id);
            if (pendingGrids && pendingGrids.length > 0 && app?.navigation) {
                pendingGrids.forEach(g => {
                    if (!app.navigation.continuesStack.includes(g)) {
                        app.navigation.push(g);
                    }
                });
            }
            this.checkBookmarkOrVideoResume(vodInfo)
            platform?.textToSpeach(`Playing ${vodInfo.title}`)
        }
    }

    $handleLongEnter() {
        Log.info('MainGrid:', '_handleLongPress')
        
        const { appConfig } = AppData as AppSetup;
        const rowType = this._getCurrentRowType()

        // Disable options menu for mid carousel, non-click and grid-banner rows
        if ((appConfig?.enableMidCarousel && (rowType === 'carousel' || rowType === 'non-click')) || rowType === 'gridbanner') {
            Log.info('MainGrid: Options menu disabled for mid carousel/non-click/gridbanner rows')
            return true
        }

        Router.focusWidget('OptionsMenu')
    }

    $handleOptionsKey() {
        const { appState, appConfig } = AppData as AppSetup
        const app = appState?.getApp()
        const currentGrid = app?.navigation.getCurrentGrid()
        
        if(currentGrid?.type === GRIDTYPE.TOP_CAROUSEL) {
            Log.info('MainGrid: In TopCarousel state, options menu disabled')
            return true
        }
        
        const rowType = this._getCurrentRowType()

        // Disable options menu for mid carousel, non-click and grid-banner rows
        if ((appConfig?.enableMidCarousel && (rowType === 'carousel' || rowType === 'non-click')) || rowType === 'gridbanner') {
            Log.info('MainGrid: Options menu disabled for mid carousel/non-click/gridbanner rows')
            return true
        }

        Router.focusWidget('OptionsMenu')
        return true
    }

    $openL1(){
        this._handleLeft()
    }

    override _handleLeft() {
        Log.info('MainGrid:', '_handleLeft')
        this.autoPlay?.userInteracted(false)
        this.isMenuFocused = true;
        Router.focusWidget('Menu')
        return true
    }

    override _handleBack(e: KeyboardEvent): boolean | void {
        Log.info('MainGrid:_handleBack ')
        /* Fix for XFIN-1190 */
        if (this.tag('NodeNotFoundMsgBox').visible) {
            this.showHideMessage(false)
            this._getFocused()
            return
        }
        if (this.autoPlay && (this.autoPlay.isAutoplayStart || this.autoPlay.isLiveAutoplayPending)) {
            this.autoPlay.registerBackPress();
            this.updateAutoPlayVideo(false)
            this.autoPlay?.stop()
            this.autoPlay.isAutoplayStart = false
            Log.info('MainGrid:_handleBack - autoplay stopped, press back again to focus Menu')
            Logger.processUserAction('heroImageAutoplayStopped', '')
            // return true
        }
        /* Fix for XFIN-1190 */
        // if(this.isTopCarouselRowAvailable){       // XFIN-1511
        //     this._setState('TopCarouselState')
        //     return true
        // 
        this.isMenuFocused = true
        Router.focusWidget('Menu')
        return true
    }

    override _handleRight() {
        Log.info('MainGrid:', '_handleRight')
        return false
    }

    override _detach() {
        clearTimeout(this.loadingTimeoutId);
        this.stage.gc(false)
        // Remove event listeners from gridViewModel
        if (this.gridViewModel) {
            this.gridViewModel.off('loaded', this._gridLoadedHandler, this)
            this.gridViewModel.off('focused', this._gridFocusedHandler, this)
            this.gridViewModel.off('selected', this._gridSelectedHandler, this)
        }
        // Remove Autoplay event listeners
        this.resetAutoplay()
    }

    static override _states(): Lightning.Component.Constructor[] {
        return [
            class TopCarouselState extends this {
                override _getFocused(): Lightning.Component | null | undefined {
                    Log.info('MainGrid: TopCarouselState _getFocused')
                    
                    // Stop any autoplay when entering TopCarousel state
                    if (this.autoPlay) {
                        this.updateAutoPlayVideo(false)
                        this.autoPlay.userInteracted(false)
                    }
                    
                    const {appState} = AppData as AppSetup
                    const app = appState?.getApp()
                    if(app?.topCarouselGrid && !app?.navigation.continuesStack.includes(app.topCarouselGrid)){
                        app?.navigation.push(app.topCarouselGrid)
                    }
                    
                    this.patch({
                        Logo: {
                            smooth: { y: [126, { duration: 0.3, timingFunction: 'ease-out' }] },
                        },
                        ItemInfo: {
                            smooth: { alpha: [0, { duration: 0.3, timingFunction: 'ease-out' }] },
                        },
                        TopCarousel: {
                            smooth: { alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }] },
                            visible: true,
                        },
                        RowListWrapper: {
                            smooth: { 
                                y: [710, { duration: 0.3, timingFunction: 'ease-out' }],
                                //alpha: [0.66, { duration: 0.3, timingFunction: 'ease-out' }]
                            },
                        }
                    })
                    return this.tag('TopCarousel')!
                }

                override _handleDown(){
                    const {appState} = AppData as AppSetup
                    const app = appState?.getApp()
                    if(app?.navigation.getCurrentGrid().type === GRIDTYPE.TOP_CAROUSEL){
                        app?.navigation.continuesStack.pop()
                    }
                    this._setState('MultiRowGridState')
                }

                $pointerEnterGrid() {
                    const {appState} = AppData as AppSetup
                    const app = appState?.getApp()
                    if(app?.navigation.getCurrentGrid()?.type === GRIDTYPE.TOP_CAROUSEL){
                        app?.navigation.continuesStack.pop()
                    }
                    this._setState('MultiRowGridState')
                }

                _handleScroll(obj: any): boolean | void {
                    if (obj?.wheelDeltaY && obj.wheelDeltaY < 0) {
                        const {appState} = AppData as AppSetup
                        const app = appState?.getApp()
                        if (app?.navigation.getCurrentGrid().type === GRIDTYPE.TOP_CAROUSEL) {
                            app?.navigation.continuesStack.pop()
                        }
                        this._setState('MultiRowGridState')
                        return true
                    }
                    return false
                }
            },
            class MultiRowGridState extends this {
                override _getFocused(): Lightning.Component | null | undefined {
                    Log.info('MainGrid: MultiRowGridState _getFocused')

                    const { appConfig } = AppData as AppSetup;
                    const rowType = this._getCurrentRowType()
                    const isGridBannerRow = rowType === 'gridbanner'

                    // Check if mid carousel is enabled in configuration
                    if (appConfig?.enableMidCarousel || isGridBannerRow) {
                        // Mid carousel is enabled (or this is a grid-banner row) - check row types and apply specific styles
                        const isCarouselRow = rowType === 'carousel'
                        const isNonClickRow = rowType === 'non-click'

                        if (isCarouselRow || isGridBannerRow) {
                            Log.info('MainGrid: Carousel/grid-banner row detected, applying mid carousel patches')
                            // Mid carousel state: hide ItemInfo and push RowListWrapper to top
                            this.patch({
                                Logo: {
                                    visible: true,
                                    smooth: { 
                                        y: [66, { duration: 0.3, timingFunction: 'ease-out' }],
                                        alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }]
                                    },
                                },
                                ItemInfo: {
                                    visible: true,
                                    smooth: { alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }] },
                                    background: this._moreBgImage || this._bgImage,
                                    showDetails: false
                                },
                                TopCarousel: {
                                    smooth: { alpha: [0, { duration: 0.3, timingFunction: 'ease-out' }] },
                                },
                                RowListWrapper: {
                                    h: 1080, // Full screen height for mid carousel
                                    smooth: { 
                                        y: [isGridBannerRow ? 120 : 160, { duration: 0.3, timingFunction: 'ease-out' }],
                                        alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }]
                                    },
                                }
                            })
                        } else if (isNonClickRow) {
                            Log.info('MainGrid: Non-click row detected, applying mid carousel patches with hero visible')
                            // Non-click row: same as mid carousel but keep ItemInfo visible
                            this.patch({
                                Logo: {
                                    visible: true,
                                    smooth: { 
                                        y: [66, { duration: 0.3, timingFunction: 'ease-out' }],
                                        alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }]
                                    },
                                },
                                ItemInfo: {
                                    visible: true,
                                    smooth: { alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }] },
                                    background: this._bgImage,
                                    showDetails: true
                                },
                                TopCarousel: {
                                    smooth: { alpha: [0, { duration: 0.3, timingFunction: 'ease-out' }] },
                                },
                                RowListWrapper: {
                                    h: 565, // Normal height to show hero image area
                                    smooth: { 
                                        y: [515, { duration: 0.3, timingFunction: 'ease-out' }],
                                        alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }]
                                    },
                                }
                            })
                        } else {
                            // Normal grid row when mid carousel is enabled
                            this.patch({
                                ItemInfo: { visible: true, showDetails: true, background: this._bgImage },
                                Logo: { visible: true }
                            })
                            
                            this.patch({
                                Logo: {
                                    smooth: { 
                                        y: [66, { duration: 0.3, timingFunction: 'ease-out' }],
                                        alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }]
                                    },
                                },
                                ItemInfo: {
                                    smooth: { alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }] },
                                },
                                TopCarousel: {
                                    smooth: { alpha: [0, { duration: 0.3, timingFunction: 'ease-out' }] },
                                },
                                RowListWrapper: {
                                    h: 565, // Normal height for regular grid
                                    smooth: { 
                                        y: [515, { duration: 0.3, timingFunction: 'ease-out' }],
                                        alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }]
                                    },
                                }
                            })
                        }
                    } else {
                        // Mid carousel is disabled - apply standard grid behavior for all rows
                        Log.info('MainGrid: Mid carousel is disabled, using standard grid layout')
                        this.patch({
                            ItemInfo: { visible: true, showDetails: true, background: this._bgImage },
                            Logo: { visible: true }
                        })
                        
                        this.patch({
                            Logo: {
                                smooth: { 
                                    y: [66, { duration: 0.3, timingFunction: 'ease-out' }],
                                    alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }]
                                },
                            },
                            ItemInfo: {
                                smooth: { alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }] },
                            },
                            TopCarousel: {
                                smooth: { alpha: [0, { duration: 0.3, timingFunction: 'ease-out' }] },
                            },
                            RowListWrapper: {
                                h: 565, // Normal height for regular grid
                                smooth: { 
                                    y: [515, { duration: 0.3, timingFunction: 'ease-out' }],
                                    alpha: [1, { duration: 0.3, timingFunction: 'ease-out' }]
                                },
                            }
                        })
                    }
                    
                    return this.tag('MultiRowGrid')!
                }

                $handleUp(){
                    if (this.autoPlay) {
                        this.updateAutoPlayVideo(false)
                        this.autoPlay.userInteracted(false)
                    }
                    if(this.isTopCarouselRowAvailable){
                        this._setState('TopCarouselState')
                    }
                }

                _handleScroll(obj: any): boolean | void {
                    const multiRowGrid = this.tag('MultiRowGrid') as any
                    if (obj?.wheelDeltaY && obj.wheelDeltaY > 0) {
                        multiRowGrid?.onUp()
                    } else {
                        multiRowGrid?.onDown()
                    }
                    return true
                }
            }
        ]
    }

    showHideMessage(visible: boolean) {
        this.patch({
            NodeNotFoundMsgBox: {
                visible: visible,
                MsgBox: {
                    text: {
                        text: "We're sorry, the title you're looking for is no longer available. But we're loading our selection for you! There's plenty more to enjoy, hope you like it."
                    }
                }
            }
        })
    }

    _handleTouchStart(details, component) {
        Log.info('MainGrid: _handleTouchStart : ', details, component)
        if((component.__ref == 'Background' || component.__ref == 'RowListWrapper' || component.__ref == 'Wrapper') && this.isMenuFocused) {
            this.widgets.menu.hideMenu()
        }
    }

    _handleHover(target) {
        // console.log('MainGrid Hover: ',  target)
        //this._focus()
        //this.fireAncestors('$handleHover', target)
    }

    // _handleUnhover(target) {
    //     console.log('MainGrid Unhover ', target)
    //     //this._unfocus()
    // }
}
